/**
 * IdentityLinksCard — paired contacts ↔ contractors with link state.
 * One-click link with typed "LINK" confirm; duplicate flagging.
 *
 * Backend:
 *   GET  /api/admin/identity-links
 *   POST /api/admin/identity-links/link  {contact_id, contractor_id, confirm:"LINK"}
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, Link2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATE_TONE = {
  linked:                { cls: 'bg-emerald-100 text-emerald-900', label: 'Linked' },
  same_email_unlinked:   { cls: 'bg-amber-100 text-amber-900', label: 'Same email · unlinked' },
  same_email_duplicates: { cls: 'bg-rose-100 text-rose-900', label: 'DUPLICATES' },
  contractor_only:       { cls: 'bg-slate-100 text-slate-700', label: 'Contractor only' },
  contact_only:          { cls: 'bg-slate-100 text-slate-700', label: 'Contact only' },
};

function authHeader() {
  const t = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function IdentityLinksCard({ isSuperAdmin }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState(null);
  const [filter, setFilter] = useState('');

  const fetchLinks = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/admin/identity-links`, { headers: authHeader() });
      setRows(r.data?.rows || []);
      setCounts(r.data?.counts || {});
    } catch (e) { /* silent */ }
    finally { setBusy(false); }
  };

  useEffect(() => { if (isSuperAdmin) fetchLinks(); }, [isSuperAdmin]);

  const onLink = async (row) => {
    if (!row.contact_id || !row.contractor_id) return;
    const confirmWord = window.prompt(
      `Link contact "${row.contact_name}" ↔ contractor "${row.contractor_company || row.contractor_name}"?\nType LINK to confirm.`,
    );
    if (confirmWord !== 'LINK') return;
    setLinking(`${row.contact_id}:${row.contractor_id}`);
    try {
      await axios.post(`${API}/api/admin/identity-links/link`, {
        contact_id: row.contact_id,
        contractor_id: row.contractor_id,
        confirm: 'LINK',
      }, { headers: authHeader() });
      await fetchLinks();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || 'Link failed');
    } finally { setLinking(null); }
  };

  if (!isSuperAdmin) return null;

  const filtered = rows.filter((r) => !filter || [
    r.contractor_company, r.contractor_name, r.contractor_email,
    r.contact_name, r.contact_email,
  ].some((v) => v && String(v).toLowerCase().includes(filter.toLowerCase())));

  return (
    <div className="p-5 rounded-xl border border-slate-300 bg-white mb-6" data-testid="identity-links-card">
      <div className="flex items-start gap-3 mb-3">
        <Link2 className="w-5 h-5 text-slate-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-semibold">Identity Links · contacts ↔ contractors</h3>
          <p className="text-sm text-slate-700">
            Every contact + contractor paired by explicit link or same email. Link any pair
            in one click with a typed <code className="bg-slate-100 px-1 rounded">LINK</code> confirm.
            Duplicate-email rows flagged.
          </p>
          {counts && (
            <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
              {Object.entries(counts).map(([k, v]) => (
                <Badge key={k} variant="outline" className={STATE_TONE[k]?.cls || ''} data-testid={`identity-links-count-${k}`}>
                  {STATE_TONE[k]?.label || k}: {v}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={fetchLinks} disabled={busy} data-testid="identity-links-refresh-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>

      <input
        data-testid="identity-links-filter-input"
        className="w-full mb-2 px-2 py-1 border rounded text-sm"
        placeholder="Filter by name / company / email…"
        value={filter} onChange={(e) => setFilter(e.target.value)}
      />

      <div className="max-h-[400px] overflow-y-auto border rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr className="text-left">
              <th className="p-2">State</th>
              <th className="p-2">Contractor</th>
              <th className="p-2">Contact</th>
              <th className="p-2">Email</th>
              <th className="p-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((row, i) => {
              const tone = STATE_TONE[row.state] || STATE_TONE.contact_only;
              return (
                <tr key={i} className="border-b hover:bg-slate-50" data-testid={`identity-links-row-${i}`}>
                  <td className="p-2">
                    <Badge className={tone.cls}>{tone.label}</Badge>
                    {row.state === 'same_email_duplicates' && (
                      <div className="text-[10px] text-rose-800 mt-1">
                        <AlertTriangle className="w-3 h-3 inline mr-0.5" />
                        {row.duplicate_count} rows w/ same email
                      </div>
                    )}
                  </td>
                  <td className="p-2 font-mono">
                    {row.contractor_company ? <div className="font-medium">{row.contractor_company}</div> : null}
                    <div className="text-slate-600">{row.contractor_name || '—'}</div>
                  </td>
                  <td className="p-2 font-mono">{row.contact_name || '—'}</td>
                  <td className="p-2 font-mono text-slate-700">{row.contractor_email || row.contact_email || '—'}</td>
                  <td className="p-2 text-right">
                    {row.state === 'same_email_unlinked' && row.contact_id && row.contractor_id && (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => onLink(row)}
                        disabled={linking === `${row.contact_id}:${row.contractor_id}`}
                        data-testid={`identity-links-link-btn-${i}`}
                      >
                        {linking === `${row.contact_id}:${row.contractor_id}` ? '…' : 'Link'}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filtered.length > 200 && (
        <div className="text-xs text-slate-500 mt-2">Showing first 200 of {filtered.length}. Refine the filter to narrow.</div>
      )}
    </div>
  );
}
